import React from 'react';
import { AppStatusLockedReason } from '~/graphql/types';
import AppEnabled from './screens/AppEnabled';
import AppDisabled from './screens/AppDisabled';
import { RouteComponentProps } from '@reach/router';
import { ExtendedAppConfig } from '~/hooks/useApps';
import usePrevious from '~/hooks/usePrevious';
import useUserRights from '~/hooks/useUserRights';

export type AppSetupModalProps = {
  /** App config data */
  app: ExtendedAppConfig;

  /** Enable or disable app */
  onChangeAppState: () => Promise<void>;

  /** Starts the oAuth flow. On error is called if the oAuth mutation returns an error */
  onOAuthCall?: (params: { onError: () => void }) => Promise<void>;

  /** Close modal */
  onClose: () => void;
};

export type Props = AppSetupModalProps & RouteComponentProps;

const AppSetupModal: React.FC<Props> = ({
  app,
  onClose,
  onChangeAppState,
  onOAuthCall,
}) => {
  const { update: mayUpdate } = useUserRights({ category: 'Apps' });

  const lockedState = app.appStatus.locked || null;

  const disabled =
    !mayUpdate ||
    lockedState === AppStatusLockedReason.Disabling ||
    app.visibility === 'preview';

  const props = {
    app,
    disabled,
    onClose,
    onChangeAppState,
  };

  // In order to delay `AppEnabled` modal to be opened right after appStatus is changed
  // When app is just being enabled, we want to show setup wizard first
  const prevEnabled = usePrevious(app.appStatus.enabled);

  if (app.appStatus.enabled && prevEnabled) {
    return <AppEnabled {...props} />;
  }

  return <AppDisabled {...props} onOAuthCall={onOAuthCall} />;
};

export default AppSetupModal;
